import { useState, useCallback, useRef } from 'react'
import { ACCEPTED_FILE_TYPES } from '@/types/upload'

export interface DragDropZoneProps {
  onFilesSelected: (files: File[]) => void
  disabled?: boolean
  className?: string
}

/**
 * Drag-and-drop zone component for file selection.
 * Supports both drag-and-drop and click-to-browse functionality.
 */
export function DragDropZone({
  onFilesSelected,
  disabled = false,
  className = '',
}: DragDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)

  // Handle drag enter
  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return

      dragCounterRef.current++
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true)
      }
    },
    [disabled]
  )

  // Handle drag leave
  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return

      dragCounterRef.current--
      if (dragCounterRef.current === 0) {
        setIsDragging(false)
      }
    },
    [disabled]
  )

  // Handle drag over
  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
    },
    []
  )

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (disabled) return

      setIsDragging(false)
      dragCounterRef.current = 0

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        onFilesSelected(files)
      }
    },
    [disabled, onFilesSelected]
  )

  // Handle click to browse
  const handleClick = useCallback(() => {
    if (disabled) return
    fileInputRef.current?.click()
  }, [disabled])

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      if (files.length > 0) {
        onFilesSelected(files)
      }
      // Reset input value to allow selecting the same file again
      e.target.value = ''
    },
    [onFilesSelected]
  )

  // Build accept string for file input
  const acceptString = [
    ...ACCEPTED_FILE_TYPES.extensions,
    ...ACCEPTED_FILE_TYPES.mimeTypes,
  ].join(',')

  // Build class names
  const baseClasses =
    'relative flex flex-col items-center justify-center w-full min-h-[200px] p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer'
  const stateClasses = isDragging
    ? 'border-blue-500 bg-blue-50'
    : disabled
      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
      : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'

  return (
    <div
      className={`${baseClasses} ${stateClasses} ${className}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      data-testid="drag-drop-zone"
      data-dragging={isDragging}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptString}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
        data-testid="file-input"
      />

      {/* Upload icon */}
      <svg
        className={`w-12 h-12 mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
      </svg>

      {/* Instructions */}
      <p className={`mb-2 text-sm ${isDragging ? 'text-blue-600' : 'text-gray-500'}`}>
        {isDragging ? (
          <span className="font-semibold">Drop files here</span>
        ) : (
          <>
            <span className="font-semibold">Click to upload</span> or drag and drop
          </>
        )}
      </p>

      {/* Accepted file types hint */}
      <p className="text-xs text-gray-400">
        PDF, PNG, JPG, JPEG, or TIFF files
      </p>
    </div>
  )
}

export default DragDropZone
